// ======================================================================
// File: src/modules/ppe/components/routing/RoutingOverrideDrawer.jsx
// Purpose:
//   Controlled override of routing assignment (ERP-safe)
//   - Only APPROVED routings selectable
//   - Mandatory reason
//   - Creates NEW routing assignment (immutable snapshot)
// ======================================================================

import React, { useEffect, useState } from "react";
import ppeOrderService from "../../services/ppeOrderService";

export default function RoutingOverrideDrawer({
  item,
  open,
  onClose,
  onSuccess,
}) {
  const [routings, setRoutings] = useState([]);
  const [routingId, setRoutingId] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingRoutings, setLoadingRoutings] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------------
  // Load APPROVED routings for this Product Family
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    async function loadRoutings() {
      setLoadingRoutings(true);
      setError(null);

      try {
        const res = await ppeOrderService.listApprovedRoutings(
          item.product_family_id
        );

        setRoutings(res || []);
      } catch (e) {
        console.error("Failed to load routings", e);
        setError("Unable to load approved routings");
        setRoutings([]);
      } finally {
        setLoadingRoutings(false);
      }
    }

    loadRoutings();
  }, [open, item.product_family_id]);

  // ------------------------------------------------------------------
  // Submit override
  // ------------------------------------------------------------------
  async function submit() {
    if (!routingId || !reason.trim()) {
      alert("Routing selection and reason are mandatory");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await ppeOrderService.overrideRouting(item.id, {
        routing_master_id: Number(routingId),
        reason: reason.trim(),
      });

      // Reload routing view
      if (onSuccess) {
        await onSuccess();
      }

      // Reset state
      setRoutingId("");
      setReason("");

      onClose();
    } catch (e) {
      console.error("Routing override failed", e);
      setError(
        e?.response?.data?.message ||
          "Failed to override routing. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  // ------------------------------------------------------------------
  // Guard
  // ------------------------------------------------------------------
  if (!open) return null;

  return (
    <div className="drawer-backdrop">
      <div className="drawer">
        {/* ================= HEADER ================= */}
        <h5 className="mb-3">
          Override Routing – Item #{item.item_seq_no}
        </h5>

        {/* ================= INFO ================= */}
        <div className="alert alert-warning small">
          <strong>Warning:</strong> Overriding routing will create a new
          execution snapshot. Existing routing history will remain unchanged.
        </div>

        {/* ================= ERROR ================= */}
        {error && (
          <div className="alert alert-danger py-2">
            {error}
          </div>
        )}

        {/* ================= ROUTING SELECT ================= */}
        <div className="mb-3">
          <label className="form-label">
            Select Approved Routing <span className="text-danger">*</span>
          </label>

          {loadingRoutings ? (
            <div className="text-muted small">Loading routings…</div>
          ) : (
            <select
              className="form-select"
              value={routingId}
              onChange={(e) => setRoutingId(e.target.value)}
            >
              <option value="">-- Select Routing --</option>

              {routings.length === 0 && (
                <option disabled>
                  No approved routings available
                </option>
              )}

              {routings.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.routing_code} – {r.routing_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* ================= REASON ================= */}
        <div className="mb-3">
          <label className="form-label">
            Reason for Override <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this routing is being overridden"
          />
        </div>

        {/* ================= ACTIONS ================= */}
        <div className="d-flex justify-content-end">
          <button
            className="btn btn-secondary me-2"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className="btn btn-danger"
            disabled={loading || loadingRoutings}
            onClick={submit}
          >
            {loading ? "Overriding…" : "Override Routing"}
          </button>
        </div>
      </div>
    </div>
  );
}
